import {
  getPointMultiplier,
  postPointMultiplierDraft,
  postPointMultiplierPublish,
  getPointMultiplierDetails,
  getPointMultiplierCsvFile,
  updatePointMultiplierDraft,
} from '@/services/pointMultiplier'
import {useQueries, useMutation} from '@tanstack/react-query'
import colConfig from '@/services/MockData/PointMultiplier/PointMultiplierColConfig.json'
import {useAppDispatch, useAppSelector} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {setInitialPointMultiplierName, setPointMultiplierFormData} from '@/store/slices/pointMultiplier'
import { useNavigate, useParams} from 'react-router-dom'
import {ROUTES} from '@/utils/constants/routesConstants'
import {getDateFromISOString, getFormattedDateString, getUUID} from '@/utils/commons'
import {useDataTable} from '@/hooks/useDataTable-hook'
import {useCallback, useMemo, useEffect,  useState} from 'react'
import {GetFormattedPointMultiplierData} from '@/models/PointMultiplierModel'
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {isArray} from 'lodash'
import {getFormattedDateFromString} from '@/helpers/dateUtils'
import {validate} from 'uuid'
export const usePointMultiplier = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const pointMultiplier = useAppSelector(state => state?.pointMultiplier)
  const initialPointMultiplierName = pointMultiplier?.initialPointMultiplierName
  const {setPageIndex, setSortingObj, setSearchArry, pageIndex, sortingObj, searchArr, handleResetAll} = useDataTable()


  const { id } = useParams(); 
  const [loading, setLoading] = useState(false);
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    }

    params.append('pageSize', 10)
    params.append('page', pageIndex || 0)

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])

  const [{data, isLoading}] = useQueries({
    queries: [
      {
        queryKey: ['POINT_MULTIPLIER', queryParams, searchArr],
        queryFn: () => getPointMultiplier(queryParams, searchArr),
      },
    ],
  })

  const pointMultiplierDraftMutation = useMutation({
    mutationKey: ['POINT_MULTIPLIER', 'draft'],
    mutationFn: data => postPointMultiplierDraft({data}),
  })

  const pointMultiplierDraftUpdateMutation = useMutation({
    mutationKey: ['POINT_MULTIPLIER', 'draft'],
    mutationFn: data => updatePointMultiplierDraft({data}),
  })

  const pointMultiplierPublishMutation = useMutation({
    mutationKey: ['POINT_MULTIPLIER', 'publish'],
    mutationFn: data => postPointMultiplierPublish({data}),
  })

  const handlePublish = async formData => {
    try {
      const cleanFormData = removeUndefinedFields(formData)
      const formattedFormData = formatFormData(cleanFormData)
      const response = await pointMultiplierPublishMutation.mutateAsync({
        ...formattedFormData,
        status: 'Published',
      })
      if (response?.success) {
        dispatch(setToastNotifications({show: true, message: 'New point multiplier published', type: 'info'}))
        dispatch(setPointMultiplierFormData({}))
        navigate(ROUTES.POINT_MULTIPLIER)
        return {success: true}
      } else {
        const errMsg = 'Point multiplier failed to publish'
        dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
        return {success: false, error: errMsg}
      }
    } catch (error) {
      console.log('Point multiplier publish error', error)
      const errMsg = 'Point multiplier failed to publish'
      dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
      return {success: false, error: errMsg}
    }
  }

  const removeUndefinedFields = obj => {
    const cleanObj = {}
    Object.keys(obj).forEach(key => {
      if (obj[key] !== undefined) {
        if (typeof obj[key] === 'object' && obj[key] !== null && !isArray(obj[key])) {
          const nestedClean = removeUndefinedFields(obj[key])
          if (Object.keys(nestedClean).length > 0) {
            cleanObj[key] = nestedClean
          }
        } else {
          cleanObj[key] = obj[key]
        }
      }
    })
    delete cleanObj['showError']
    delete cleanObj['_persist']
    return cleanObj
  }

  const formatFormData = formData => {
    if ('activeEndDate' in formData) {
      formData['endDate'] = getFormattedDateString(formData.activeEndDate)
    }
    if ('activeStartDate' in formData) {
      formData['startDate'] = getFormattedDateString(formData.activeStartDate)
    }
    if ('modifierName' in formData) {
      formData['multiplierName'] = formData.modifierName
    }
    delete formData['activeEndDate']
    delete formData['activeStartDate']
    delete formData['modifierName']

    if ('endDate' in formData) {
      formData['hasEndDate'] = true
    } else {
      formData['hasEndDate'] = false
    }

    if (formData.rulesCriteria === 'Products') {
      delete formData.applicationId
    }

    return {
      ...formData,
    }
  }

  const handleDraft = async formData => {
    const isPointMultiplierNameChanged =
      initialPointMultiplierName === null || initialPointMultiplierName !== formData?.multiplierName
    const cleanFormData = removeUndefinedFields(formData)
    const formattedFormData = formatFormData(cleanFormData)
    try {
      const requestData = {
        ...formattedFormData,
        status: 'draft',
        uuid: isPointMultiplierNameChanged
          ? getUUID(formattedFormData?.multiplierName)
          : getUUID(initialPointMultiplierName),
      }
      let response
      if (isPointMultiplierNameChanged) {
        response = await pointMultiplierDraftMutation.mutateAsync(requestData)
      } else {
        response = await pointMultiplierDraftUpdateMutation.mutateAsync(requestData)
      }

      if (response?.success) {
        dispatch(setToastNotifications({show: true, message: 'New point multiplier saved', type: 'info'}))
        dispatch(setPointMultiplierFormData({}))
        navigate(ROUTES.POINT_MULTIPLIER)
        return {success: true}
      } else {
        const errMsg = 'Point multiplier failed to save'
        dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
        return {success: false, error: errMsg}
      }
    } catch (error) {
      console.log('Point multiplier save error', error)
      const errMsg = 'Point multiplier failed to save'
      dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
      return {success: false, error: errMsg}
    }
  }

  const resultsData = useMemo(() => data || {}, [data])
  const pointMultiplierData = useMemo(() => GetFormattedPointMultiplierData(resultsData), [resultsData])

  const handleGetPointMultiplierDetails = useCallback(async () => {
    const queryParams = validate(id)? `uuid=${id}` : `id=${id}`
    try {
        setLoading(true)
        const d = await getPointMultiplierDetails(queryParams)
        setLoading(false)
        return d
    } catch {
      setLoading(false)
    }
  
  }, [id])

  const processNavigationFormData = useCallback(
    navFormData => {
      if (!navFormData) return null

      const {multiplierName, startDate, endDate, ...restData} = navFormData
      const updatedFormData = {
        ...restData,
        modifierName: multiplierName || navFormData.modifierName,
        activeStartDate: getFormattedDateFromString(startDate),
        activeEndDate: getFormattedDateFromString(endDate),
      }

      dispatch(setInitialPointMultiplierName(navFormData?.multiplierName))
      dispatch(setPointMultiplierFormData(updatedFormData))
      return updatedFormData
    },
    [dispatch]
  )

  const getPointMultiplierDetailsById = useCallback(async () => {
    let result = await handleGetPointMultiplierDetails()
    result = result.draftPointMultipliers[0];
    const updatedResponse = {
      rulesCriteria: result?.rulesCriteria,
      activeStartDate: getDateFromISOString(result?.startDate),
      modifierName: result?.multiplierName,
      category: result?.category,
      rulesPriority: result?.rulesPriority,
      multiplierValue: result?.multiplierValue,
      segment: result?.segment,
      channel: result?.channel,
      activeEndDate: getDateFromISOString(result?.endDate),
      hasEndDate: result?.hasEndDate,
      metadata: result?.metadata,
      status: result?.status,
    }
    dispatch(setPointMultiplierFormData(updatedResponse))
  }, [handleGetPointMultiplierDetails, dispatch])

  useEffect(() => {
    if (id) {
      getPointMultiplierDetailsById()
    }
  }, [getPointMultiplierDetailsById, id])

  const onDownloadBtnClick = async selectedIds => {
    const body = {
      ids: selectedIds.join(','),
      filters: searchArr,
    }
    const data = await getPointMultiplierCsvFile(body)
    const fileName = `PointMultiplier_${getDateWithTimeString()}`
    downloadCSVFiles(data, fileName)
  }

  return {
    pointMultiplierData,
    pointMultiplierColConfigData: colConfig,
    handleDraft,
    handlePublish,
    isDraftLoading: pointMultiplierDraftMutation.isPending,
    pageIndex,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    searchArr,
    isLoading,
    onDownloadBtnClick,
    processNavigationFormData,
    handleResetAll,
    loading
  }
}
